// Brand claiming utility for converting anonymous brands to authenticated user brands
import { supabase } from './supabase';

export interface ClaimBrandResult {
    brand_id: string;
    message: string;
}

// Global state to prevent duplicate claiming attempts
const claimingInProgress = new Set<string>();

export interface AnonymousBrandData {
    id: string;
    url?: string;
    scraping_status: string;
    brand_analysis_status: string;
    brand_data: {
        brand_name?: string;
        elevator_pitch?: string;
        target_market?: string;
        value_props?: string;
        differentiators?: string;
        brand_voice?: string;
        call_to_action?: string;
        niche?: string;
        target_geography?: string;
        products_overview?: string;
        b2b_b2c?: string;
        conversion_assumption?: number;
        clickthru_assumption?: number;
    };
}

/**
 * Claims an anonymous brand for the authenticated user
 */
export async function claimAnonymousBrand(sessionToken: string): Promise<ClaimBrandResult> {
    console.log('[claimBrand] Starting brand claim process for session token:', sessionToken);

    // Check if claiming is already in progress for this session token
    if (claimingInProgress.has(sessionToken)) {
        console.log('[claimBrand] Claiming already in progress for this session token, skipping duplicate request');
        throw new Error('Brand claiming already in progress');
    }

    // Mark claiming as in progress
    claimingInProgress.add(sessionToken);

    try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
            console.error('[claimBrand] No authenticated session found');
            throw new Error('Not authenticated');
        }

        console.log('[claimBrand] Authenticated user found:', session.user.id);

        const requestUrl = `${import.meta.env.VITE_WORKERS_URL}/rainfactory/anonymous-brands/claim_brand`;
        console.log('[claimBrand] Making request to:', requestUrl);

        const response = await fetch(requestUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({ sessionToken }),
        });

        console.log('[claimBrand] Response status:', response.status);
        console.log('[claimBrand] Response headers:', Object.fromEntries(response.headers.entries()));

        const result = await response.json();
        console.log('[claimBrand] Response body:', result);

        if (!response.ok) {
            console.error('[claimBrand] API error:', result.error || 'Failed to claim brand');
            throw new Error(result.error || 'Failed to claim brand');
        }

        console.log('[claimBrand] Brand claim successful! Cleaning up session tokens...');

        // Clean up stored session token
        localStorage.removeItem('brand_claim_session_token');
        localStorage.removeItem('anonymous_session_token');

        console.log('[claimBrand] Returning result:', result);
        return result;
    } catch (error) {
        console.error('[claimBrand] Error during brand claiming:', error);
        throw error;
    } finally {
        // Always remove from claiming in progress set
        claimingInProgress.delete(sessionToken);
    }
}

/**
 * Fetches anonymous brand data by session token
 */
export async function fetchAnonymousBrandData(sessionToken: string): Promise<AnonymousBrandData> {
    const response = await fetch(
        `${import.meta.env.VITE_WORKERS_URL}/rainfactory/anonymous-brands/get_processing_status`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ sessionToken }),
        }
    );

    const result = await response.json();
    if (!response.ok) {
        throw new Error(result.error || 'Failed to fetch anonymous brand data');
    }

    return result;
}

/**
 * Gets stored session token for brand claiming
 */
export function getStoredClaimSessionToken(): string | null {
    return localStorage.getItem('brand_claim_session_token') ||
        localStorage.getItem('anonymous_session_token');
}

/**
 * Stores session token for brand claiming
 */
export function storeClaimSessionToken(sessionToken: string): void {
    localStorage.setItem('brand_claim_session_token', sessionToken);
}

/**
 * Clears all stored session tokens
 */
export function clearStoredSessionTokens(): void {
    localStorage.removeItem('brand_claim_session_token');
    localStorage.removeItem('anonymous_session_token');
} 